import React, { useEffect, useMemo, useState } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ParentSize } from '@visx/responsive';
import { scaleOrdinal } from '@visx/scale';
import { timeFormat, timeParse } from 'd3-time-format';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// Our Own imports
import { useCan } from '@feedflo/access-control';
import ComboboxMulti from '../../atoms/ComboboxMulti';
import Heading from '../../atoms/Heading';
import Page from '../../atoms/Page';
import Select from '../../atoms/Select';
import BarStackChart from '../../organisms/BarStackChart';
import FeedFloDateRangeInput from '../../organisms/FeedFloDateRangeInput/FeedFloDateRangeInput';
import MessageHistoryTable from '../../organisms/MessageHistoryTable';
import useSearchParamsState, { useMultipleSearchParamsState } from '../../utils/hooks/useSearchParamsState';
import useUser from '../../utils/hooks/useUser';
import {
  MESSAGE_HISTORY_CHART_DATA_GQL,
  MESSAGE_HISTORY_FILTER_OPTIONS_GQL,
  MESSAGE_HISTORY_MESSAGES_GQL,
  MESSAGE_HISTORY_ORG_USERS_GQL,
} from './queries';
import {
  buildMessageWhereClause,
  FAULT_TYPE_FILTER_OPTIONS,
  messageToTableRowData,
  MODE_FILTER_OPTIONS,
  PAGE_SIZE,
  pivotChartRows,
  resolveDateRange,
  sanitizeIdList,
  selectedItemsToIds,
  SERIES_OPTIONS,
  toPostgresArrayLiteral,
  toggleSingleCategoryFilter,
  resolveFilterOptionId,
  isChartDataEmpty,
} from './messageHistoryUtils';

// Relative path imports
import MessageHistoryFilterField from './MessageHistoryFilterField';
import './MessageHistoryPage.scss';

const asUserContext = {
  headers: {
    'x-hasura-role': 'user',
  },
};

function MessageHistoryPage({ titleSegments = [] }) {
  // --- State hooks ---
  const [pageNum, setPageNum] = useState(0);

  // --- Atoms and other Hooks ---
  const { loading: privilegesLoading } = useCan();
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const setMultipleSearchParams = useMultipleSearchParamsState();

  // --- URL-driven filter state ---
  const [selectedOrgIds, setSelectedOrgIds] = useSearchParamsState('orgId', []);
  const [selectedBarnIds, setSelectedBarnIds] = useSearchParamsState('barnId', []);
  const [selectedUserIds, setSelectedUserIds] = useSearchParamsState('userId', []);
  const [selectedModes, setSelectedModes] = useSearchParamsState('mode', []);
  const [selectedTypes, setSelectedTypes] = useSearchParamsState('type', []);
  const [fromParam] = useSearchParamsState('from', null);
  const [toParam] = useSearchParamsState('to', null);
  const [presetDays] = useSearchParamsState('p', null);
  const [chartSeries, setChartSeries] = useSearchParamsState('series', 'mode');

  const selectedOrgIdsNormalized = useMemo(() => sanitizeIdList(selectedOrgIds), [selectedOrgIds]);
  const selectedBarnIdsNormalized = useMemo(() => sanitizeIdList(selectedBarnIds), [selectedBarnIds]);
  const selectedUserIdsNormalized = useMemo(() => sanitizeIdList(selectedUserIds), [selectedUserIds]);
  const selectedModesNormalized = useMemo(() => sanitizeIdList(selectedModes), [selectedModes]);
  const selectedTypesNormalized = useMemo(() => sanitizeIdList(selectedTypes), [selectedTypes]);

  // --- Privileges and Access Hooks ---
  const canViewMessageHistory = true;

  // --- Derived state ---
  const dateRange = useMemo(() => {
    if (fromParam && toParam) {
      return resolveDateRange(fromParam, toParam);
    }
    if (presetDays) {
      return {
        from: dayjs.tz().subtract(presetDays - 1, 'days').startOf('day'),
        to: dayjs.tz().endOf('day'),
      };
    }
    return resolveDateRange(null, null);
  }, [fromParam, toParam, presetDays]);

  const selectedTypesNumeric = useMemo(
    () => selectedTypesNormalized.map((t) => parseInt(t, 10)).filter((t) => !Number.isNaN(t)),
    [selectedTypesNormalized],
  );

  const whereClause = useMemo(
    () =>
      buildMessageWhereClause({
        selectedOrgIds: selectedOrgIdsNormalized,
        selectedBarnIds: selectedBarnIdsNormalized,
        selectedUserIds: selectedUserIdsNormalized,
        selectedModes: selectedModesNormalized,
        selectedTypes: selectedTypesNumeric,
        dateFrom: dateRange.from.unix(),
        dateTo: dateRange.to.unix(),
      }),
    [
      selectedOrgIdsNormalized,
      selectedBarnIdsNormalized,
      selectedUserIdsNormalized,
      selectedModesNormalized,
      selectedTypesNumeric,
      dateRange.from,
      dateRange.to,
    ],
  );

  const typeFilterOptions = FAULT_TYPE_FILTER_OPTIONS;

  // --- Apollo/GraphQL hooks ---
  const { data: filterOptionsData } = useQuery(MESSAGE_HISTORY_FILTER_OPTIONS_GQL, {
    skip: privilegesLoading || !canViewMessageHistory,
    context: asUserContext,
  });

  const orgOptions = useMemo(() => {
    const farms = filterOptionsData?.farm || [];
    const orgMap = new Map();

    farms.forEach((farm) => {
      if (!farm.organization_id) {
        return;
      }
      if (!orgMap.has(farm.organization_id)) {
        const org = filterOptionsData?.organization?.find((item) => item.id === farm.organization_id);
        orgMap.set(farm.organization_id, {
          id: farm.organization_id,
          name: org?.name || 'Unknown organization',
        });
      }
    });

    return [...orgMap.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [filterOptionsData]);

  const hasMultipleOrgs = orgOptions.length > 1;

  const orgIdsForUserQuery = useMemo(() => {
    if (selectedOrgIdsNormalized.length > 0) {
      return selectedOrgIdsNormalized;
    }
    return orgOptions.map((o) => o.id);
  }, [selectedOrgIdsNormalized, orgOptions]);

  const { data: orgUsersData } = useQuery(MESSAGE_HISTORY_ORG_USERS_GQL, {
    variables: { organization_ids: orgIdsForUserQuery },
    skip: privilegesLoading || !canViewMessageHistory || orgIdsForUserQuery.length === 0,
    context: asUserContext,
  });

  const barnOptions = useMemo(() => {
    let farms = filterOptionsData?.farm || [];
    if (selectedOrgIdsNormalized.length > 0) {
      farms = farms.filter((farm) => selectedOrgIdsNormalized.includes(farm.organization_id));
    }
    return farms.map((farm) => ({ id: farm.id, name: farm.name }));
  }, [filterOptionsData, selectedOrgIdsNormalized]);

  const userOptions = useMemo(() => {
    const usersMap = new Map();
    (orgUsersData?.barn_users || []).forEach((row) => {
      if (row.user?.id) {
        usersMap.set(row.user.id, {
          id: row.user.id,
          name: row.user.name || row.user.email || 'Unknown user',
        });
      }
    });
    (orgUsersData?.organization_users || []).forEach((row) => {
      if (row.user?.id) {
        usersMap.set(row.user.id, {
          id: row.user.id,
          name: row.user.name || row.user.email || 'Unknown user',
        });
      }
    });
    return [...usersMap.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [orgUsersData]);

  const {
    data: messagesData,
    loading: messagesLoading,
    networkStatus: messagesNetworkStatus,
    error: messagesError,
  } = useQuery(MESSAGE_HISTORY_MESSAGES_GQL, {
    variables: {
      limit: PAGE_SIZE + 1,
      offset: pageNum * PAGE_SIZE,
      where: whereClause,
      order_by: [{ sent_at: 'desc_nulls_last' }, { created_at: 'desc_nulls_last' }],
    },
    skip: privilegesLoading || !canViewMessageHistory,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    context: asUserContext,
  });

  const { data: chartData, loading: chartLoading, error: chartError } = useQuery(MESSAGE_HISTORY_CHART_DATA_GQL, {
    variables: {
      p_from: dateRange.from.unix(),
      p_to: dateRange.to.unix(),
      p_time_zone: user?.timezone || 'UTC',
      p_series_by: chartSeries,
      p_org_ids: toPostgresArrayLiteral(selectedOrgIdsNormalized),
      p_barn_ids: toPostgresArrayLiteral(selectedBarnIdsNormalized),
      p_user_ids: toPostgresArrayLiteral(selectedUserIdsNormalized),
      p_modes: toPostgresArrayLiteral(selectedModesNormalized, { quoteStrings: true }),
      p_types: toPostgresArrayLiteral(selectedTypesNumeric),
    },
    skip: privilegesLoading || !canViewMessageHistory,
    fetchPolicy: 'network-only',
    context: asUserContext,
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    if (messagesError) {
      console.error(messagesError);
      Sentry.captureException(messagesError);
      toast('Failed to load message history', { type: 'error' });
    }
  }, [messagesError]);

  useEffect(() => {
    if (chartError) {
      console.error(chartError);
      Sentry.captureException(chartError);
      toast('Failed to load chart data', { type: 'error' });
    }
  }, [chartError]);

  useEffect(() => {
    setPageNum(0);
  }, [
    selectedOrgIdsNormalized,
    selectedBarnIdsNormalized,
    selectedUserIdsNormalized,
    selectedModesNormalized,
    selectedTypesNormalized,
    fromParam,
    toParam,
    presetDays,
  ]);

  const chartPivot = useMemo(
    () => pivotChartRows(chartData?.message_history_chart_data || [], chartSeries),
    [chartData, chartSeries],
  );

  const isChartEmpty = useMemo(() => isChartDataEmpty(chartPivot), [chartPivot]);

  const colorScale = scaleOrdinal({
    domain: chartPivot.keys,
    range: ['#7dabf8', '#e4db84', '#d17b7b', '#97c58b', '#b794f4', '#f6ad55', '#68d391', '#fc8181'],
  });

  const tableLoading =
    messagesLoading ||
    messagesNetworkStatus === NetworkStatus.refetch ||
    messagesNetworkStatus === NetworkStatus.setVariables;

  const tableRows = useMemo(() => {
    const messages = messagesData?.message || [];
    return messages
      .slice(0, PAGE_SIZE)
      .map((message) => messageToTableRowData(message, { selectedOrgIds: selectedOrgIdsNormalized }))
      .filter(Boolean);
  }, [messagesData, selectedOrgIdsNormalized]);

  const hasNextPage = (messagesData?.message || []).length > PAGE_SIZE;
  const itemTotalCount = pageNum * PAGE_SIZE + tableRows.length + (hasNextPage ? 1 : 0);

  const pageTitleSegments = useMemo(() => ['Message History', ...titleSegments], [titleSegments]);

  const hasActiveFilters = useMemo(
    () =>
      selectedOrgIdsNormalized.length > 0 ||
      selectedBarnIdsNormalized.length > 0 ||
      selectedUserIdsNormalized.length > 0 ||
      selectedModesNormalized.length > 0 ||
      selectedTypesNormalized.length > 0 ||
      Boolean(fromParam || toParam || presetDays) ||
      chartSeries !== 'mode',
    [
      selectedOrgIdsNormalized,
      selectedBarnIdsNormalized,
      selectedUserIdsNormalized,
      selectedModesNormalized,
      selectedTypesNormalized,
      fromParam,
      toParam,
      presetDays,
      chartSeries,
    ],
  );

  const hasCustomDateRange = Boolean(fromParam || toParam || presetDays);

  // --- Function definitions ---
  const handleOrgChange = (selectedItems) => {
    setSelectedOrgIds(selectedItemsToIds(selectedItems));
    setPageNum(0);
  };

  const handleBarnChange = (selectedItems) => {
    setSelectedBarnIds(selectedItemsToIds(selectedItems));
    setPageNum(0);
  };

  const handleUserChange = (selectedItems) => {
    setSelectedUserIds(selectedItemsToIds(selectedItems));
    setPageNum(0);
  };

  const handleModeChange = (selectedItems) => {
    setSelectedModes(selectedItemsToIds(selectedItems));
    setPageNum(0);
  };

  const handleTypeChange = (selectedItems) => {
    setSelectedTypes(selectedItemsToIds(selectedItems));
    setPageNum(0);
  };

  const handleSeriesChange = (event) => {
    setChartSeries(event.target.value);
  };

  const handleChartCategoryClick = (seriesKey) => {
    setPageNum(0);

    switch (chartSeries) {
      case 'mode':
        setSelectedModes(toggleSingleCategoryFilter(selectedModesNormalized, seriesKey));
        break;
      case 'type':
        setSelectedTypes(toggleSingleCategoryFilter(selectedTypesNormalized, seriesKey));
        break;
      case 'user':
        setSelectedUserIds(
          toggleSingleCategoryFilter(
            selectedUserIdsNormalized,
            resolveFilterOptionId(seriesKey, userOptions),
          ),
        );
        break;
      case 'barn':
        setSelectedBarnIds(
          toggleSingleCategoryFilter(
            selectedBarnIdsNormalized,
            resolveFilterOptionId(seriesKey, barnOptions),
          ),
        );
        break;
      default:
        break;
    }
  };

  const handleDateRangeChange = (event) => {
    if (event.dateRange) {
      setMultipleSearchParams({
        from: dayjs.tz(event.dateRange.from).unix(),
        to: dayjs.tz(event.dateRange.to).unix(),
        p: undefined,
      });
      setPageNum(0);
    }
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setPageNum(0);
  };

  const clearSearchParamKeys = (...keys) => {
    const keysToRemove = new Set(keys);
    setSearchParams(Object.fromEntries([...searchParams.entries()].filter(([key]) => !keysToRemove.has(key))));
    setPageNum(0);
  };

  const handleClearDateRange = () => clearSearchParamKeys('from', 'to', 'p');

  const handleClearOrgFilter = () => {
    setSelectedOrgIds(undefined);
    setPageNum(0);
  };

  const handleClearBarnFilter = () => {
    setSelectedBarnIds(undefined);
    setPageNum(0);
  };

  const handleClearUserFilter = () => {
    setSelectedUserIds(undefined);
    setPageNum(0);
  };

  const handleClearModeFilter = () => {
    setSelectedModes(undefined);
    setPageNum(0);
  };

  const handleClearTypeFilter = () => {
    setSelectedTypes(undefined);
    setPageNum(0);
  };

  const handleClearChartSeries = () => {
    setChartSeries(undefined);
  };

  const xAxisValueAccessor = (data) => data.date;

  const xAxisValues = () => chartPivot.data.map(xAxisValueAccessor);

  const xAxisValueFormatter = (tickValue) => {
    const parseTime = timeParse('%Y-%m-%d');
    const formatTime = timeFormat('%b %d');
    const parsed = parseTime(tickValue);
    return parsed ? formatTime(parsed) : tickValue;
  };

  const formatChartCount = (value) => String(Math.round(value ?? 0));

  // --- Render ---
  if (privilegesLoading) {
    return (
      <Page className="MessageHistoryPage" titleSegments={pageTitleSegments}>
        <div className="MessageHistoryPage-content">
          <div className="MessageHistoryPage-header">
            <div className="MessageHistoryPage-headerTitleBlock">
              <Heading className="MessageHistoryPage-title" text="Message History" />
              <p className="MessageHistoryPage-headerSubtitle">
                Review fault notifications sent to users across your organization.
              </p>
            </div>
          </div>
          <div className="MessageHistoryPage-loading">Loading…</div>
        </div>
      </Page>
    );
  }

  if (!canViewMessageHistory) {
    return (
      <Page className="MessageHistoryPage" titleSegments={pageTitleSegments}>
        <div className="MessageHistoryPage-content">
          <div className="MessageHistoryPage-header">
            <div className="MessageHistoryPage-headerTitleBlock">
              <Heading className="MessageHistoryPage-title" text="Message History" />
              <p className="MessageHistoryPage-headerSubtitle">
                Review fault notifications sent to users across your organization.
              </p>
            </div>
          </div>
          <div>You do not have permission to access this page.</div>
        </div>
      </Page>
    );
  }

  return (
    <Page className="MessageHistoryPage" titleSegments={pageTitleSegments}>
      <div className="MessageHistoryPage-content">
        <div className="MessageHistoryPage-header">
          <div className="MessageHistoryPage-headerTitleBlock">
            <Heading className="MessageHistoryPage-title" text="Message History" />
            <p className="MessageHistoryPage-headerSubtitle">
              Review fault notifications sent to users across your organization.
            </p>
          </div>
          <div className="MessageHistoryPage-headerActions">
            <MessageHistoryFilterField
              className="MessageHistoryPage-filterField--date"
              hasSelection={hasCustomDateRange}
              onClear={handleClearDateRange}
              clearLabel="Clear date range"
            >
              <FeedFloDateRangeInput
                from={dateRange.from}
                to={dateRange.to}
                max={dayjs.tz()}
                onChange={handleDateRangeChange}
              />
            </MessageHistoryFilterField>
            <button
              className="MessageHistoryPage-resetFilters"
              type="button"
              disabled={!hasActiveFilters}
              onClick={handleClearFilters}
              title="Return to the default 30-day view and clear all selections"
            >
              Reset filters
            </button>
          </div>
        </div>

        <section className="MessageHistoryPage-filtersPanel" aria-label="Message filters">
          <div className="MessageHistoryPage-filtersPanelLabel">Filters</div>
          <div className="MessageHistoryPage-filtersRow">
            <div className="MessageHistoryPage-filterGroup">
              {hasMultipleOrgs && (
                <MessageHistoryFilterField
                  className="MessageHistoryPage-filterField--org"
                  hasSelection={selectedOrgIdsNormalized.length > 0}
                  onClear={handleClearOrgFilter}
                  clearLabel="Clear org filter"
                >
                  <ComboboxMulti
                    items={orgOptions}
                    value={selectedOrgIdsNormalized}
                    onChange={handleOrgChange}
                    label="Org"
                    selectedItemsOnTop
                  />
                </MessageHistoryFilterField>
              )}
              <MessageHistoryFilterField
                className="MessageHistoryPage-filterField--barn"
                hasSelection={selectedBarnIdsNormalized.length > 0}
                onClear={handleClearBarnFilter}
                clearLabel="Clear barn filter"
              >
                <ComboboxMulti
                  items={barnOptions}
                  value={selectedBarnIdsNormalized}
                  onChange={handleBarnChange}
                  label="Barn"
                  selectedItemsOnTop
                />
              </MessageHistoryFilterField>
              <MessageHistoryFilterField
                className="MessageHistoryPage-filterField--user"
                hasSelection={selectedUserIdsNormalized.length > 0}
                onClear={handleClearUserFilter}
                clearLabel="Clear user filter"
              >
                <ComboboxMulti
                  items={userOptions}
                  value={selectedUserIdsNormalized}
                  onChange={handleUserChange}
                  label="User"
                  selectedItemsOnTop
                />
              </MessageHistoryFilterField>
            </div>
            <div className="MessageHistoryPage-filterGroup">
              <MessageHistoryFilterField
                className="MessageHistoryPage-filterField--mode"
                hasSelection={selectedModesNormalized.length > 0}
                onClear={handleClearModeFilter}
                clearLabel="Clear notification methods filter"
              >
                <ComboboxMulti
                  items={MODE_FILTER_OPTIONS}
                  value={selectedModesNormalized}
                  onChange={handleModeChange}
                  label="Notification Methods"
                  selectedItemsOnTop
                />
              </MessageHistoryFilterField>
              <MessageHistoryFilterField
                className="MessageHistoryPage-filterField--type"
                hasSelection={selectedTypesNormalized.length > 0}
                onClear={handleClearTypeFilter}
                clearLabel="Clear type filter"
              >
                <ComboboxMulti
                  items={typeFilterOptions}
                  value={selectedTypesNormalized}
                  onChange={handleTypeChange}
                  label="Type"
                  selectedItemsOnTop
                />
              </MessageHistoryFilterField>
            </div>
          </div>
        </section>

        <section className="MessageHistoryPage-chartSection" aria-label="Message history chart">
          <div className="MessageHistoryPage-chartHeader">
            <h2 className="MessageHistoryPage-chartHeading">Trend</h2>
            <MessageHistoryFilterField
              className="MessageHistoryPage-filterField--chartSeries"
              hasSelection={chartSeries !== 'mode'}
              onClear={handleClearChartSeries}
              clearLabel="Clear chart series"
            >
              <Select
                label="Chart series"
                value={chartSeries}
                onChange={handleSeriesChange}
                itemList={SERIES_OPTIONS}
              />
            </MessageHistoryFilterField>
          </div>
          <div className="MessageHistoryPage-barStackChartContainer">
            {chartLoading ? (
              <div className="MessageHistoryPage-chartLoading">Loading chart…</div>
            ) : isChartEmpty ? (
              <div className="MessageHistoryPage-chartEmpty" role="status">
                <p className="MessageHistoryPage-chartEmptyTitle">No message data for this chart</p>
                <p className="MessageHistoryPage-chartEmptyDescription">
                  No messages match the current filters and date range. Try widening the date range or clearing filters.
                </p>
              </div>
            ) : (
              <ParentSize debounceTime={10}>
                {({ width, height }) => (
                  <BarStackChart
                    width={width}
                    height={height}
                    data={chartPivot.data}
                    keys={chartPivot.keys}
                    keyNameMap={chartPivot.keyNameMap}
                    xAxisValueAccessor={xAxisValueAccessor}
                    xAxisValues={xAxisValues()}
                    xAxisValueFormatter={xAxisValueFormatter}
                    yAxisValues={chartPivot.yAxisValues}
                    tooltipTitleAccessor={xAxisValueAccessor}
                    tooltipTitleFormatter={xAxisValueFormatter}
                    colorScale={colorScale}
                    valueFormatter={formatChartCount}
                    onChartClick={handleChartCategoryClick}
                  />
                )}
              </ParentSize>
            )}
          </div>
        </section>

        <section className="MessageHistoryPage-tableSection" aria-label="Message history table">
          <h2 className="MessageHistoryPage-tableHeading">Messages</h2>
          <MessageHistoryTable
            rows={tableLoading ? [] : tableRows}
            loading={tableLoading}
            pageNum={pageNum}
            maxRows={PAGE_SIZE}
            itemTotalCount={itemTotalCount}
            onClickNext={() => setPageNum((p) => p + 1)}
            onClickPrevious={() => setPageNum((p) => Math.max(0, p - 1))}
          />
        </section>
      </div>
    </Page>
  );
}

export default MessageHistoryPage;
